/**
 * The first real request, and what its failure means.
 *
 * Holding a token is not a session. `GET /v1/organizations/me` is where the service
 * verifies the signature against the issuer's JWKS, checks `aud` and `iss`, resolves the
 * tenant from the claim, and answers with the organization that claim named. Until it has,
 * the shell knows nothing it could put in front of a person, and it renders a splash
 * rather than a layout with empty chrome.
 *
 * Every outcome routes through `diagnose`, so the 401/503 split the service went to the
 * trouble of making survives all the way to the screen:
 *
 *   401 invalid_token        → the credential is bad; go to the issuer (once, guarded)
 *   401 unauthenticated,     → the service never looked at the token; a configuration
 *       token sent             message, and NO redirect, because none would help
 *   403 tenant_claim_missing → the token is fine and names no organization; explain
 *   503                      → retried by the query client with backoff, banner meanwhile
 *   network                  → the service is not there
 */

import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, type ReactNode } from 'react';
import { useOrganization } from '@inai/inventory-core/react';

import { tokenWasSent } from '../api';
import { useAuth } from '../auth/context';
import { diagnose } from '../auth/diagnose';
import { FaultNotice } from './FaultNotice';
import { Splash } from './Splash';

export function SessionGate({ children }: { children: ReactNode }) {
  const { reportFault, confirmSession, signOut, fault: sessionFault } = useAuth();
  const organization = useOrganization();

  const fault = useMemo(
    () => (organization.error ? diagnose(organization.error, tokenWasSent()) : null),
    [organization.error],
  );

  useEffect(() => {
    if (fault) reportFault(fault);
    else if (organization.isSuccess) confirmSession();
  }, [fault, organization.isSuccess, reportFault, confirmSession]);

  // While a retryable failure is being retried, TanStack keeps the query PENDING and
  // leaves `error` null until the last attempt fails — so `error` is the wrong thing to
  // read here and a first check against it reported nothing at all, which is how this was
  // found. `failureReason` is the one that is populated between attempts, and saying "the
  // identity provider is unreachable, attempt 3" is the entire point of answering 503
  // rather than 401: the person is told to wait, and their session is not thrown away.
  const interim = organization.failureReason
    ? diagnose(organization.failureReason, tokenWasSent())
    : null;

  if (organization.isPending && !fault) {
    return (
      <Splash
        message={
          interim
            ? `${interim.message} Attempt ${organization.failureCount + 1}.`
            : 'Resolving your organization…'
        }
      />
    );
  }

  if (fault) {
    // `credential` is handled by the session, which has already left for the issuer.
    if (fault.kind === 'credential') return <Splash message="Signing in again…" />;
    return (
      <FaultNotice
        fault={fault}
        onRetry={() => void organization.refetch()}
        onSignOut={signOut}
      />
    );
  }

  // A verified token naming a tenant the service has no organization row for. Rare, and
  // worth its own words: it is not an authentication problem and signing in again will
  // produce exactly the same answer.
  if (organization.data === null) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', mt: 6, px: 3 }}>
        <Alert severity="warning" variant="outlined">
          <Typography sx={{ fontSize: 13 }}>
            Your token resolved a tenant this service has no organization row for. The
            credential is fine; the organization has not been created here yet, or this
            deployment is pointed at a different database than the one that holds it.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button size="small" variant="contained" onClick={() => void organization.refetch()}>
              Check again
            </Button>
            <Button size="small" variant="outlined" onClick={signOut}>
              Sign out
            </Button>
          </Stack>
        </Alert>
      </Box>
    );
  }

  return (
    <>
      {/* A 503 that arrived from somewhere else — a background refetch, a posting — is a
          banner rather than a takeover. The session is still good; the service is not
          able to say so at this instant. */}
      {sessionFault?.kind === 'unavailable' ? (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Alert severity="warning" variant="outlined" sx={{ mt: 1, fontSize: 13 }}>
            {sessionFault.message}
          </Alert>
        </Box>
      ) : null}
      {children}
    </>
  );
}
